import { useMemo } from 'react'
import { getLanguages } from '@/app/languages'
import { getCurrencies } from '@/app/currencies'
import { Currency } from '@/app/types'
import { HIDDEN_CURRENCY_WHEN_LOCAL_NOT_IN_LIST } from '@/app/constants'

// TODO: Check me
export function useAllLanguages(sortAlphabetically = false) {
	return useMemo(() => {
		const languages = getLanguages()
		if (sortAlphabetically) {
			return [...languages].sort((a, b) =>
				a.englishName.localeCompare(b.englishName)
			)
		}
		return languages
	}, [sortAlphabetically])
}

export function useAllCurrencies() {
	return useMemo(() => getCurrencies(), [])
}

interface UseFilteredLanguagesOptions {
	searchQuery: string
	fallbackToAll?: boolean
	sortAlphabetically?: boolean
}

export function useFilteredLanguages({
	searchQuery,
	fallbackToAll = true,
	sortAlphabetically = false,
}: UseFilteredLanguagesOptions) {
	const allLanguages = useAllLanguages(sortAlphabetically)

	return useMemo(() => {
		if (!searchQuery.trim()) {
			return allLanguages
		}

		const lowerQuery = searchQuery.toLowerCase()
		const filtered = allLanguages.filter(
			(lang) =>
				lang.name.toLowerCase().includes(lowerQuery) ||
				lang.englishName.toLowerCase().includes(lowerQuery) ||
				lang.code.toLowerCase().includes(lowerQuery) ||
				lang.lang.toLowerCase().includes(lowerQuery)
		)

		// If sorting is enabled, maintain alphabetical order in filtered results
		if (sortAlphabetically && filtered.length > 0) {
			return [...filtered].sort((a, b) =>
				a.englishName.localeCompare(b.englishName)
			)
		}

		return filtered.length > 0 || !fallbackToAll ? filtered : allLanguages
	}, [searchQuery, allLanguages, fallbackToAll, sortAlphabetically])
}

export function useFilteredCurrencies(
	searchQuery: string,
	fallbackToAll = true,
	localCurrency?: Currency | null
) {
	const allCurrencies = useAllCurrencies()

	// Prepend local currency if it's not already in the list
	// Hide INR when a non-standard local currency is added
	const currenciesWithLocal = useMemo(() => {
		if (!localCurrency) return allCurrencies

		const allCurrenciesSet = new Set(allCurrencies.map((c) => c.abbreviation))
		if (allCurrenciesSet.has(localCurrency.abbreviation)) {
			return allCurrencies
		}

		// Local currency doesn't exist in predefined list - hide INR
		const currenciesWithoutINR = allCurrencies.filter((c) => c.abbreviation !== HIDDEN_CURRENCY_WHEN_LOCAL_NOT_IN_LIST)
		return [localCurrency, ...currenciesWithoutINR]
	}, [allCurrencies, localCurrency])

	return useMemo(() => {
		if (!searchQuery.trim()) {
			return currenciesWithLocal
		}

		const lowerQuery = searchQuery.toLowerCase()
		const filtered = currenciesWithLocal.filter(
			(currency) =>
				currency.label.toLowerCase().includes(lowerQuery) ||
				currency.abbreviation.toLowerCase().includes(lowerQuery)
		)

		return filtered.length > 0 || !fallbackToAll ? filtered : currenciesWithLocal
	}, [searchQuery, currenciesWithLocal, fallbackToAll])
}

